import { Injectable } from '@angular/core';
import { Observable, of, shareReplay } from 'rxjs';
import { EvideInputContentFormService } from '@app/services/input-contents/evide-input-content-form.service';

/**
 * Cache wrapper around the cheap `GetFormVariantLabelByGuid` endpoint. Every form-fill
 * screen renders the variant selector, so without a cache we'd fire one request per
 * selector instance per page render. Forms' variant labels rarely change, so a simple
 * per-session in-memory Map is enough — keyed by formGuid.
 *
 * `shareReplay(1)` deduplicates concurrent callers while the request is in flight, and
 * keeps the resolved value cached for any later subscriber.
 */
@Injectable({ providedIn: 'root' })
export class FormVariantLabelService {

  private readonly cache = new Map<string, Observable<string | null>>();

  constructor(private formService: EvideInputContentFormService) { }

  public getLabel(formGuid: string | null | undefined): Observable<string | null> {
    if (!formGuid) return of(null);
    const cached = this.cache.get(formGuid);
    if (cached) return cached;
    const obs = this.formService.getFormVariantLabelByGuid(formGuid).pipe(shareReplay(1));
    this.cache.set(formGuid, obs);
    return obs;
  }

  /** Invalidate a single form's cached label — call this when the admin re-saves the form
   *  so the next consumer fetch picks up the new label without a full page reload. */
  public invalidate(formGuid: string): void {
    this.cache.delete(formGuid);
  }
}
